import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, ShoppingCart, Sprout, TrendingUp, DollarSign, Clock, Shield, BarChart3 } from 'lucide-react';

const SBAGrocery = () => {
  return (
    <div className="bg-[#FAFAFA]">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/264636/pexels-photo-264636.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0C2340]/95 via-[#0C2340]/85 to-[#0C2340]/70" />
        </div>
        <div className="absolute inset-0 noise-overlay" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 w-full py-28">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-amber-500/15 backdrop-blur-sm border border-amber-400/20 rounded-full px-4 py-1.5 mb-8">
              <Shield className="h-4 w-4 text-amber-300" />
              <span className="text-xs font-semibold text-amber-200 uppercase tracking-wider">SBA Backed Program</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              SBA Grocery<br /><span className="text-amber-300">Program</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-xl">
              Government backed financing for agriculture, food distribution, grocery retail, and related supply chain businesses. Designed to provide long term working capital and expansion funding with lower monthly payments.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/apply" data-testid="sba-grocery-apply" className="inline-flex items-center gap-2 bg-amber-500 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-amber-400 transition-all">
                Check Eligibility <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-white/10 transition-all">Talk to Us</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights Strip */}
      <section className="bg-[#0C2340] py-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {[
              { icon: <DollarSign className="h-5 w-5 text-amber-400" />, value: 'Up to $1M', label: 'Working Capital' },
              { icon: <TrendingUp className="h-5 w-5 text-amber-400" />, value: 'Up to $5M', label: 'Expansion Capital' },
              { icon: <Clock className="h-5 w-5 text-amber-400" />, value: '10 Year', label: 'Term Structure' },
              { icon: <BarChart3 className="h-5 w-5 text-amber-400" />, value: 'Lower', label: 'Monthly Payments' },
              { icon: <Shield className="h-5 w-5 text-amber-400" />, value: 'SBA', label: 'Government Backed' },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  {stat.icon}
                </div>
                <div>
                  <div className="text-sm font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>{stat.value}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For + Use of Funds */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Who It's For */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Who This Program Serves</h2>
              <p className="text-slate-500 mb-10 leading-relaxed">
                The SBA Grocery Program is structured for businesses operating within the food and agriculture ecosystem. If your business touches the supply chain between farm and table, this program was designed with you in mind.
              </p>
              <div className="space-y-4">
                {[
                  { icon: <ShoppingCart className="h-5 w-5" />, title: 'Grocery Retail', desc: 'Independent grocers, specialty food stores, and supermarket operators.' },
                  { icon: <Sprout className="h-5 w-5" />, title: 'Agriculture and Farming', desc: 'Farming operations, agricultural producers, and related agribusiness.' },
                  { icon: <TrendingUp className="h-5 w-5" />, title: 'Food Distribution', desc: 'Wholesale distributors, food service suppliers, and logistics providers.' },
                  { icon: <BarChart3 className="h-5 w-5" />, title: 'Supply Chain Services', desc: 'Cold storage, packaging, processing, and related support businesses.' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4 p-5 bg-white border border-slate-200 rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 mb-0.5">{item.title}</h3>
                      <p className="text-xs text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Use of Funds + Benefits */}
            <div className="space-y-8">
              <div className="relative rounded-2xl overflow-hidden h-52">
                <img src="https://images.pexels.com/photos/2733918/pexels-photo-2733918.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Agriculture" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0C2340]/90 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>Structured for Stability</div>
                  <p className="text-xs text-amber-100/80 mt-1">Long term capital solutions designed to replace short term debt and improve cash flow.</p>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Use of Funds</h3>
                {[
                  'Working capital to stabilize and grow operations',
                  'Expansion into new locations or markets',
                  'Refinance high cost short term debt',
                  'Equipment, inventory, and infrastructure investment',
                  'Acquisition or consolidation of operations',
                  'Real estate purchase or improvement',
                ].map((use) => (
                  <div key={use} className="flex items-center gap-3 mb-4 last:mb-0">
                    <CheckCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <span className="text-sm text-slate-600">{use}</span>
                  </div>
                ))}
              </div>

              <Link to="/apply" className="block bg-[#0C2340] text-white rounded-2xl p-8 group hover:bg-[#0e2a4a] transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Check Your Eligibility</h3>
                    <p className="text-sm text-slate-400">Submit your request to receive a structured approval</p>
                  </div>
                  <ArrowRight className="h-6 w-6 text-amber-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Program Benefits</h2>
            <p className="text-slate-500 max-w-xl mx-auto">Government backed capital structured for long term growth and cash flow improvement.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: <DollarSign className="h-6 w-6" />, title: 'Cash Flow Relief', desc: 'Lower monthly payments compared to short term financing, freeing up operating capital for reinvestment and day to day operations.', color: 'bg-amber-50 text-amber-700' },
              { icon: <Clock className="h-6 w-6" />, title: 'Long Term Structure', desc: '10 year terms provide predictable payment schedules and the stability to plan for growth without constant refinancing cycles.', color: 'bg-blue-50 text-blue-700' },
              { icon: <TrendingUp className="h-6 w-6" />, title: 'Scalable Capital', desc: 'Access up to $5M for expansion and fixed assets, or up to $1M in working capital to support scaling at the pace your business demands.', color: 'bg-emerald-50 text-emerald-700' },
            ].map((card) => (
              <div key={card.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
                <div className={`w-12 h-12 rounded-xl ${card.color} flex items-center justify-center mb-6`}>
                  {card.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{card.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Qualifier + CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/2252584/pexels-photo-2252584.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0C2340]/90" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-amber-500/15 border border-amber-400/20 rounded-full px-4 py-1.5 mb-6">
            <Shield className="h-4 w-4 text-amber-300" />
            <span className="text-xs font-semibold text-amber-200 uppercase tracking-wider">SBA Government Backed</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Ready to Explore the SBA Grocery Program?</h2>
          <p className="text-base text-slate-400 mb-4 max-w-xl mx-auto">Eligibility is determined through a quick review process. Submit your request to receive a structured approval.</p>
          <p className="text-sm text-slate-500 mb-10 max-w-lg mx-auto italic">Long term capital solutions designed to replace short term debt and improve cash flow.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/apply" className="inline-flex items-center gap-2 bg-amber-500 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-amber-400 transition-colors">Check Eligibility <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/10 transition-colors">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SBAGrocery;
